<template>
  <div class="tabbed-report-viewer">
    <!-- Ícone flutuante quando minimizado -->
    <div v-if="isMinimized && tabs.length > 0">
      <v-fab
        ref="minimizedFab"
        icon="mdi-chart-box"
        color="primary"
        size="large"
        location="top end"
        class="floating-icon"
        @click="restoreModal"
        @mouseenter="onMinimizedFabEnter"
        @mouseleave="onMinimizedFabLeave"
      >
        <v-badge
          :content="tabs.length"
          color="red"
          floating
        >
        </v-badge>
      </v-fab>

      <transition name="fade-transition">
        <div
          v-if="minimizedMenuOpen"
          class="minimized-menu-card"
          :style="minimizedMenuStyle"
          @mouseenter="onMinimizedMenuEnter"
          @mouseleave="onMinimizedMenuLeave"
        >
          <div class="minimized-menu-content">
            <div class="text-h6 mb-2">Relatórios Abertos ({{ tabs.length }})</div>
            <div v-for="(tab, index) in tabs" :key="tab.id" class="text-caption">
              {{ index + 1 }}. {{ tab.report.name }}
            </div>
            <div class="text-caption mt-2 text-grey-lighten-1">
              Clique para restaurar
            </div>
            <v-divider class="my-2"></v-divider>
            <v-btn
              size="small"
              color="error"
              variant="text"
              prepend-icon="mdi-close"
              class="close-all-btn"
              @click.stop="handleCloseAllFromMenu"
            >
              Fechar Todos
            </v-btn>
          </div>
        </div>
      </transition>
    </div>

    <!-- Modal principal -->
    <v-dialog 
      v-model="showDialog" 
      :fullscreen="!isMinimized"
      :max-width="isMinimized ? '400px' : '100vw'"
      :max-height="isMinimized ? '300px' : '100vh'"
      :persistent="!isMinimized"
      scrollable
      :retain-focus="false"
      :z-index="isMinimized ? 1000 : 9999"
      :eager="true"
    >
      <v-card class="report-modal-card" :class="{ 'minimized': isMinimized }">
        <!-- Header com abas -->
        <v-toolbar class="report-toolbar">
          <!-- Botão de fechar -->
          <v-btn icon @click="closeAllTabs" class="mr-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          
          <!-- Abas dos relatórios -->
          <div class="tabs-container" v-if="tabs.length > 0">
            <!-- Desktop: Abas normais -->
            <v-tabs
              v-model="activeTabIndex"
              class="report-tabs d-none d-md-flex"
              show-arrows
              slider-color="var(--accent)"
            >
              <v-tab
                v-for="(tab, index) in tabs"
                :key="tab.id"
                :value="index"
                class="report-tab"
                @click="switchToTab(index)"
              >
                <div class="tab-content">
                  <span class="tab-title">{{ tab.report.name }}</span>
                  <v-btn
                    icon="mdi-close"
                    size="x-small"
                    class="tab-close-btn"
                    @click.stop="closeTab(index)"
                  ></v-btn>
                </div>
              </v-tab>
            </v-tabs>
            
            <!-- Mobile: Menu dropdown -->
            <div class="mobile-tabs d-flex d-md-none align-center">
              <v-menu offset-y>
                <template v-slot:activator="{ props }">
                  <v-tooltip
                    :text="currentTab?.report.name || 'Relatórios'"
                    location="bottom"
                    :disabled="!currentTab?.report.name"
                  >
                    <template v-slot:activator="{ props: tooltipProps }">
                      <v-btn
                        v-bind="{ ...props, ...tooltipProps }"
                        variant="text"
                        color="white"
                        class="mobile-tab-btn"
                      >
                        <v-icon class="mr-2">mdi-menu-down</v-icon>
                        <span class="mobile-tab-name">{{ currentTab?.report.name || 'Relatórios' }}</span>
                        <v-badge
                          :content="tabs.length"
                          color="red"
                          floating
                          class="ml-2"
                        ></v-badge>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>
                <v-list class="mobile-tab-list">
                  <v-list-item
                    v-for="(tab, index) in tabs"
                    :key="tab.id"
                    @click="switchToTab(index)"
                    :class="{ 'active-tab': index === activeTabIndex }"
                  >
                    <v-tooltip
                      :text="tab.report.name"
                      location="right"
                    >
                      <template v-slot:activator="{ props: tooltipProps }">
                        <v-list-item-title 
                          v-bind="tooltipProps"
                          class="mobile-tab-title"
                        >
                          {{ tab.report.name }}
                        </v-list-item-title>
                      </template>
                    </v-tooltip>
                    <template v-slot:append>
                      <v-btn
                        icon="mdi-close"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click.stop="closeTab(index)"
                      ></v-btn>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
          
          <v-spacer></v-spacer>
          
          <!-- Botões de ação -->
          <v-btn icon @click="toggleMinimize" class="mr-2">
            <v-icon>{{ isMinimized ? 'mdi-window-maximize' : 'mdi-window-minimize' }}</v-icon>
          </v-btn>
          
          <v-btn icon @click="refreshCurrentReport" v-if="currentTab">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
        
        <!-- Conteúdo dos relatórios (mantém iframes montados; alterna com v-show) -->
        <v-card-text class="pa-0 report-content" v-if="tabs.length > 0">
          <div
            v-for="(tab, index) in tabs"
            :key="tab.id"
            class="tab-pane"
            v-show="index === activeTabIndex"
          >
            <div v-if="tab.loading" class="d-flex justify-center align-center loading-container">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <div class="ml-4">Carregando {{ tab.report.name }}...</div>
            </div>
            
            <div v-else-if="tab.error" class="d-flex justify-center align-center error-container">
              <v-alert type="error" class="ma-4">
                {{ tab.error }}
              </v-alert>
            </div>
            
            <div v-else-if="tab.iframeSrc" class="report-iframe-container">
              <iframe
                :src="tab.iframeSrc"
                width="100%"
                height="100%"
                frameborder="0"
                allow="fullscreen"
              ></iframe>
            </div>
          </div>
        </v-card-text>
        
        <!-- Mensagem quando não há abas -->
        <v-card-text v-else class="d-flex justify-center align-center no-tabs-container">
          <v-alert type="info" variant="tonal">
            Nenhum relatório aberto. Clique em um relatório para abri-lo.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'TabbedReportViewer',
  data() {
    return {
      showDialog: false,
      tabs: [],
      activeTabIndex: 0,
      isMinimized: false,
      nextTabId: 1,
      minimizedMenuOpen: false,
      menuCloseTimeout: null,
      minimizedMenuPosition: {
        top: 0,
        left: 0
      }
    }
  },
  computed: {
    currentTab() {
      return this.tabs[this.activeTabIndex] || null
    },
    minimizedMenuStyle() {
      return {
        top: `${this.minimizedMenuPosition.top}px`,
        left: `${this.minimizedMenuPosition.left}px`
      }
    }
  },
  mounted() {
    // Limpar estado ao montar o componente
    this.closeAllTabs()
  },
  beforeUnmount() {
    this.clearMinimizedMenuTimeout()
  },
  methods: {
    onMinimizedFabEnter() {
      this.openMinimizedMenu()
    },

    onMinimizedFabLeave() {
      this.scheduleMinimizedMenuClose()
    },

    onMinimizedMenuEnter() {
      this.clearMinimizedMenuTimeout()
    },

    onMinimizedMenuLeave() {
      this.scheduleMinimizedMenuClose()
    },

    openMinimizedMenu() {
      this.clearMinimizedMenuTimeout()
      this.updateMinimizedMenuPosition()
      this.minimizedMenuOpen = true
    },

    scheduleMinimizedMenuClose() {
      this.clearMinimizedMenuTimeout()
      this.menuCloseTimeout = setTimeout(() => {
        this.minimizedMenuOpen = false
      }, 400)
    },

    clearMinimizedMenuTimeout() {
      if (this.menuCloseTimeout) {
        clearTimeout(this.menuCloseTimeout)
        this.menuCloseTimeout = null
      }
    },

    updateMinimizedMenuPosition() {
      const fabRef = this.$refs.minimizedFab
      const fabEl = fabRef?.$el || fabRef
      if (!fabEl) return
      const rect = fabEl.getBoundingClientRect()
      this.minimizedMenuPosition = {
        top: rect.top + rect.height / 2,
        left: rect.left
      }
    },

    closeMinimizedMenu() {
      this.clearMinimizedMenuTimeout()
      this.minimizedMenuOpen = false
    },

    handleCloseAllFromMenu() {
      this.closeAllTabs()
    },

    // Abrir um novo relatório em uma aba
    async openReport(report) {
      if (!report || !report.id) {
        console.error('❌ Relatório inválido:', report)
        return
      }
      
      // Se estiver minimizado, restaurar o modal primeiro
      if (this.isMinimized) {
        this.isMinimized = false
      }
      
      // Verificar se o relatório já está aberto
      const existingTabIndex = this.tabs.findIndex(tab => tab.report.id === report.id)
      
      if (existingTabIndex !== -1) {
        // Se já existe, apenas trocar para essa aba
        this.activeTabIndex = existingTabIndex
        this.showDialog = true
        return
      }
      
      // Criar nova aba
      const newTab = {
        id: this.nextTabId++,
        report: report,
        loading: true,
        error: null,
        iframeSrc: null,
        reportName: '',
        didOneTimeBackendRefresh: false,
        didWarmup: false
      }
      
      this.tabs.push(newTab)
      this.activeTabIndex = this.tabs.length - 1
      this.showDialog = true
      
      // Carregar o relatório de forma síncrona na primeira abertura
      await this.loadReport(newTab)
      // Soft refresh para estabilizar primeira renderização
      this.$nextTick(() => {
        this.softRefreshCurrentReport()
      })
    },
    
    // Carregar o conteúdo do relatório
    async loadReport(tab) {
      tab.loading = true
      tab.error = null
      tab.iframeSrc = null
      
      try {
        const response = await api.post(`/reports/${tab.report.id}/original-embed/`, null, { timeout: 45000 })
        
        if (response.data.iframe_src) {
          // Reestruturar o objeto para forçar reatividade
          const updatedTab = {
            ...tab,
            iframeSrc: response.data.iframe_src,
            reportName: response.data.report_name,
            error: null
          }
          // Substituir o objeto no array
          const tabIndex = this.tabs.findIndex(t => t.id === tab.id)
          if (tabIndex !== -1) {
            this.tabs.splice(tabIndex, 1, updatedTab)
          }
        } else {
          tab.error = 'Erro ao carregar relatório'
        }
      } catch (error) {
        console.error('Erro ao carregar embed:', error)
        tab.error = error.response?.data?.error || 'Erro ao carregar relatório'
      } finally {
        // Atualizar loading no array também
        const tabIndex = this.tabs.findIndex(t => t.id === tab.id)
        if (tabIndex !== -1) {
          this.tabs[tabIndex].loading = false
        }
      }
    },
    
    // Trocar para uma aba específica
    switchToTab(index) {
      if (index >= 0 && index < this.tabs.length) {
        this.activeTabIndex = index
      }
    },
    
    // Fechar uma aba específica
    closeTab(index) {
      if (this.tabs.length <= 1) {
        // Se é a última aba, fechar o modal
        this.closeAllTabs()
        return
      }
      
      this.tabs.splice(index, 1)
      
      // Ajustar o índice da aba ativa
      if (this.activeTabIndex >= this.tabs.length) {
        this.activeTabIndex = this.tabs.length - 1
      } else if (this.activeTabIndex > index) {
        this.activeTabIndex--
      }
    },
    
    // Fechar todas as abas
    closeAllTabs() {
      this.tabs = []
      this.activeTabIndex = 0
      this.showDialog = false
      this.isMinimized = false
      this.closeMinimizedMenu()
    },
    
    // Atualizar o relatório atual
    async refreshCurrentReport() {
      if (this.currentTab) {
        await this.loadReport(this.currentTab)
      }
    },
    
    // Recarrega o iframe atual sem chamar o backend (evita consumir novo token)
    softRefreshCurrentReport() {
      const tab = this.currentTab
      if (tab && tab.iframeSrc) {
        const src = tab.iframeSrc
        tab.iframeSrc = null
        this.$nextTick(() => {
          tab.iframeSrc = src
        })
      }
    },
    
    // Alternar entre minimizado e maximizado
    toggleMinimize() {
      this.isMinimized = !this.isMinimized
      if (this.isMinimized) {
        // Quando minimizar, fechar o modal mas manter as abas
        this.showDialog = false
        this.closeMinimizedMenu()
      } else {
        // Quando restaurar, abrir o modal
        this.showDialog = true
      }
    },
    
    // Restaurar o modal do estado minimizado
    restoreModal() {
      this.isMinimized = false
      this.showDialog = true
      this.closeMinimizedMenu()
    },
    
    // Método público para abrir o modal (usado pelas views)
    open() {
      this.showDialog = true
    },
    
    // Método público para fechar o modal
    close() {
      this.closeAllTabs()
    }
  }
}
</script>

<style scoped>
.tabbed-report-viewer {
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1000;
}

.floating-icon {
  position: fixed !important;
  top: 50% !important;
  right: 20px !important;
  z-index: 10000 !important;
  transition: transform 0.3s ease !important;
  animation: slideInFromRight 0.3s ease-out !important;
  transform: translateY(-50%);
}

.floating-icon:hover {
  transform: translateY(-50%) scale(1.1) !important;
}

.minimized-menu-card {
  position: fixed;
  z-index: 10001;
  transform: translate(calc(-100% - 12px), -50%);
  pointer-events: auto;
}

.minimized-menu-content {
  background: rgba(50, 50, 50, 0.95);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  min-width: 220px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}
.close-all-btn {
  transition: background-color 0.2s ease, color 0.2s ease;
}

.close-all-btn:hover {
  background-color: rgba(255, 82, 82, 0.15) !important;
  color: #ff5252 !important;
}

/* Responsividade do ícone flutuante */
@media (max-width: 768px) {
  .floating-icon {
    right: 15px !important;
    transform: translateY(-50%) scale(0.9) !important;
  }
  
  .floating-icon:hover {
    transform: translateY(-50%) scale(1.0) !important;
  }
}

@media (max-width: 480px) {
  .floating-icon {
    right: 10px !important;
    transform: translateY(-50%) scale(0.8) !important;
  }
  
  .floating-icon:hover {
    transform: translateY(-50%) scale(0.9) !important;
  }
}

@keyframes slideInFromRight {
  from {
    transform: translate(100px, -50%);
    opacity: 0;
  }
  to {
    transform: translate(0, -50%);
    opacity: 1;
  }
}

.report-modal-card {
  background: var(--background) !important;
  color: var(--text-primary) !important;
  z-index: 10000 !important;
}

.report-modal-card.minimized {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  border-radius: 16px;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--border-light);
}

.report-toolbar {
  background: var(--surface) !important;
  border-bottom: 1px solid var(--border-light) !important;
  color: var(--text-primary) !important;
  box-shadow: none !important;
}

[data-theme='dark'] .report-toolbar {
  background: var(--surface) !important;
}

.report-tabs {
  background: transparent !important;
  min-height: 48px;
}

.report-tab {
  min-width: 200px;
  max-width: 300px;
  padding: 0 8px !important;
  margin-right: 4px;
  background: transparent !important;
  border-radius: 12px 12px 0 0 !important;
  transition: all 0.3s ease;
  color: var(--text-secondary) !important;
  opacity: 0.7;
}

.report-tab:hover {
  background: var(--surface-hover) !important;
  opacity: 1;
}

.report-tab.v-tab--selected {
  background: var(--surface-hover) !important;
  border-bottom: 2px solid var(--accent) !important;
  color: var(--accent) !important;
  opacity: 1;
}

.tab-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.tab-close-btn {
  min-width: 24px !important;
  width: 24px !important;
  height: 24px !important;
  background: transparent !important;
  color: var(--text-tertiary) !important;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.tab-close-btn:hover {
  background: rgba(255, 82, 82, 0.1) !important;
  color: var(--error) !important;
  transform: scale(1.1);
}

.report-content {
  height: calc(100vh - 64px);
  background: var(--background);
  position: relative;
}

.report-toolbar .v-btn {
  color: var(--text-primary) !important;
}


.tabs-container {
  flex: 1;
  max-width: calc(100vw - 200px);
  overflow: hidden;
}

.tab-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.tab-pane {
  height: 100%;
  width: 100%;
  position: relative;
}

.minimized .report-content {
  height: 200px;
}

.loading-container,
.error-container,
.no-tabs-container {
  height: 100%;
  min-height: 400px;
}

.report-iframe-container {
  height: 100%;
  width: 100%;
  position: relative;
}

.report-iframe-container iframe {
  border: none;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

/* Responsividade */
@media (max-width: 768px) {
  .tabs-container {
    max-width: calc(100vw - 120px);
    overflow-x: auto;
    overflow-y: hidden;
  }
  
  .report-tab {
    min-width: 120px;
    max-width: 180px;
    padding: 0 6px !important;
    margin-right: 2px;
  }
  
  .tab-title {
    font-size: 11px;
    line-height: 1.2;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .tab-content {
    gap: 4px;
  }
  
  .tab-close-btn {
    min-width: 20px !important;
    width: 20px !important;
    height: 20px !important;
    font-size: 12px;
  }
  
  .report-toolbar {
    padding: 0 8px;
  }
}

@media (max-width: 480px) {
  .tabs-container {
    max-width: calc(100vw - 100px);
  }
  
  .report-tab {
    min-width: 100px;
    max-width: 140px;
    padding: 0 4px !important;
    margin-right: 1px;
  }
  
  .tab-title {
    font-size: 10px;
    max-width: 60px;
  }
  
  .tab-content {
    gap: 2px;
  }
  
  .tab-close-btn {
    min-width: 18px !important;
    width: 18px !important;
    height: 18px !important;
    font-size: 10px;
  }
  
  .report-toolbar {
    padding: 0 4px;
    min-height: 48px;
  }
  
  .report-toolbar .v-btn {
    min-width: 36px !important;
    width: 36px !important;
    height: 36px !important;
  }
  
  /* Ajustes específicos para mobile */
  .mobile-tab-btn {
    max-width: calc(100vw - 120px);
    padding: 6px 8px !important;
  }
  
  .mobile-tab-name {
    max-width: calc(100vw - 160px);
    font-size: 13px;
  }
  
  .mobile-tab-list {
    min-width: 200px;
    max-width: calc(100vw - 20px);
  }
  
  .mobile-tab-title {
    font-size: 13px;
    max-width: 180px;
  }
}

@media (max-width: 360px) {
  .tabs-container {
    max-width: calc(100vw - 80px);
  }
  
  .report-tab {
    min-width: 80px;
    max-width: 120px;
    padding: 0 2px !important;
  }
  
  .tab-title {
    font-size: 9px;
    max-width: 50px;
  }
  
  .tab-close-btn {
    min-width: 16px !important;
    width: 16px !important;
    height: 16px !important;
    font-size: 8px;
  }
  
  /* Ajustes para telas muito pequenas */
  .mobile-tab-btn {
    max-width: calc(100vw - 100px);
    padding: 4px 6px !important;
  }
  
  .mobile-tab-name {
    max-width: calc(100vw - 140px);
    font-size: 12px;
  }
  
  .mobile-tab-list {
    min-width: 180px;
    max-width: calc(100vw - 10px);
  }
  
  .mobile-tab-title {
    font-size: 12px;
    max-width: 160px;
  }
}

/* Estilos para menu mobile */
.mobile-tabs {
  flex: 1;
  max-width: calc(100vw - 120px);
}

.mobile-tab-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  max-width: calc(100vw - 150px);
  min-width: 120px;
  justify-content: flex-start;
  padding: 8px 12px !important;
  color: var(--text-primary) !important;
}

.mobile-tab-list {
  background: var(--surface) !important;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  min-width: 250px;
  max-width: calc(100vw - 40px);
}

.mobile-tab-title {
  color: var(--text-primary) !important;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
  white-space: normal;
  max-width: 200px;
  padding: 4px 0;
}

.active-tab {
  background: var(--surface-hover) !important;
}

.active-tab .mobile-tab-title {
  color: var(--accent) !important;
  font-weight: 600;
}


.mobile-tab-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100vw - 200px);
  display: inline-block;
}

/* Animações */
.report-modal-card {
  transition: all 0.3s ease;
}

.tab-content {
  transition: all 0.2s ease;
}

/* Scrollbar personalizada para as abas */
.report-tabs ::v-deep .v-tabs__wrapper {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.report-tabs ::v-deep .v-tabs__wrapper::-webkit-scrollbar {
  height: 4px;
}

.report-tabs ::v-deep .v-tabs__wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.report-tabs ::v-deep .v-tabs__wrapper::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.report-tabs ::v-deep .v-tabs__wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>